import { isVulcanItemHidden } from './vulcanItemHides';
import { deriveDeclarativeVisibility } from './vulcanStructuralComponents';

// Where each Contentful content type keeps its children.
const CHILD_KEYS = ['fields', 'formFields', 'formStepFields'];

/**
 * Walk the raw step tree, returning a map of every leaf field name to its
 * ANCESTOR entries.
 *
 * PERFORMANCE (SOF-3402/SOF-3439, found via React DevTools Profiler on a real
 * order after Stage 4c-ii made `values`/`stepFields` span the WHOLE order
 * instead of one step): the original used `{ ...acc, ...collectAncestors(...) }`
 * to merge each recursive call's result — a fresh full-object COPY on every
 * merge, making this O(N^2) in total field count. At single-step scale (~20-30
 * fields) that was cheap enough to be invisible; at whole-order scale
 * (100-300+ fields across every step) it measured 5.4s of self time alone
 * (`_objectSpread`, 66% of a slow keystroke's total time). Rewritten to
 * MUTATE one shared accumulator instead — same output, O(N).
 *
 * @param {Array} entries
 * @param {Array} [ancestors]
 * @returns {Object.<string, Array>}
 */
const collectAncestors = (entries, ancestors = [], acc = {}) => {
  (entries || []).forEach(entry => {
    const raw = entry && entry.fields;
    if (!raw) return;
    const children = CHILD_KEYS.map(k => raw[k]).find(Array.isArray);
    if (children) {
      collectAncestors(children, ancestors.concat([raw]), acc);
    } else if (raw.name) {
      acc[raw.name] = ancestors;
    }
  });
  return acc;
};

/**
 * A `fieldRules` rule that hides a leaf field when one of its ANCESTOR items is
 * hidden — either by Vulcan's imperative rules (`isVulcanItemHidden`) or by the
 * ancestor's OWN declarative gate (appearsAfterField/Value/backendFieldValidation).
 *
 * Those hides are applied at the RENDER layer (structural components + the
 * content-type section wrappers), which is enough to keep the markup right — but
 * the engine derives its own field state independently, so it still considered
 * those fields visible. `validateStepFields` only skips a field when
 * `state.isVisible` is false, so a REQUIRED field inside a hidden section kept
 * failing validation: `goNext()` returned false and Continue silently did
 * nothing, with the error attached to a field the user cannot see. (Confirmed:
 * a required field in a TSB 1.5 section produced
 * `errors: { hiddenRequired: 'This field is required' }` while the section was
 * correctly absent from the DOM.)
 *
 * BOTH kinds matter, and each was confirmed live as a silently-dead Continue:
 * the engine evaluates every field's OWN declarative gate, but a field with no
 * gate of its own sitting inside a gated SECTION stayed visible to validation
 * while absent from the DOM. The legacy validator checked the container too —
 * it recursed into a `formSection` only `if (sectionIsVisible(key))` and into a
 * `formFieldsGroup` only `if (isRelatedFieldExisted(fields, …))` — and validated
 * a leaf only `else if (isVisible)`. A field the user cannot see must not block
 * submission.
 *
 * Evaluated per call rather than precomputed because the hides depend on live
 * values (e.g. `existingSecurityEquipment` gates the TSB 1.5 sections); only the
 * ancestor MAP is static.
 *
 * @param {{stepFields: Array, orderData?: Object, salesRepOutlet?: string, visibilityContext?: Object}} ctx
 * @returns {(field: Object, values: Object) => boolean}
 */
export function makeHiddenAncestorPredicate({
  stepFields,
  orderData,
  salesRepOutlet,
  visibilityContext
}) {
  const ancestorsByField = collectAncestors(stepFields);

  // PERFORMANCE: the engine calls this predicate ONCE PER FIELD during a
  // single `buildFieldStates` pass, always with the SAME `values` reference
  // (it doesn't change mid-pass). Reshaping `values` into `formFields`/
  // `pseudoForm` was previously redone from scratch on every one of those
  // calls, each via the same O(N^2) spread-reduce pattern as `collectAncestors`
  // above (`hiddenAncestorRule.js:83`/`:95` in the profiler, ~90% of a slow
  // keystroke's total time combined with `collectAncestors`). Both fixed to
  // mutate a plain object (O(N) each), AND cached by `values` identity so the
  // whole reshape runs ONCE per pass instead of once per field.
  let cachedValues = null;
  let cachedFormFields = null;
  let cachedPseudoForm = null;

  return (field, values) => {
    const ancestors = ancestorsByField[field && field.name];
    if (!ancestors || !ancestors.length) return false;

    if (values !== cachedValues) {
      // `isVulcanItemHidden` reads other fields through `getFieldValue`,
      // which wants the rich bag shape; fieldRules only get raw `values`, so
      // adapt them.
      const formFields = {};
      // The declarative interpreter needs a `form`-shaped view. Field states
      // are still being built when fieldRules run, so treat every value as
      // visible — the same assumption the legacy bag made (`normalizedFields`
      // initialised every field with `isVisible: true`).
      const fieldStates = {};
      Object.keys(values || {}).forEach(k => {
        formFields[k] = { value: values[k], isVisible: true };
        fieldStates[k] = { isVisible: true };
      });
      cachedValues = values;
      cachedFormFields = formFields;
      cachedPseudoForm = { values: values || {}, fieldStates };
    }

    return ancestors.some(
      raw =>
        isVulcanItemHidden(raw, {
          formFields: cachedFormFields,
          orderData,
          salesRepOutlet
        }) ||
        !deriveDeclarativeVisibility(raw, cachedPseudoForm, visibilityContext)
    );
  };
}

export default makeHiddenAncestorPredicate;
